/**
 * AI Models Service
 * Handles API calls for AI model operations including listing, execution, and status tracking.
 */
import API from './api.ts';

// Get models assigned to a study (excludes tools)
export async function getModelsForStudy(studyId) {
  const response = await API.get(`/ai-models/models/`, {
    params: { study: studyId, entity_type: 'model' }
  });
  return response.data;
}

// Get all available models
export async function getAllModels() {
  const response = await API.get(`/ai-models/models/`);
  return response.data;
}

// Get all available models (entity_type='model' only)
export async function getModelsOnly() {
  const response = await API.get(`/ai-models/models/`, {
    params: { entity_type: 'model' }
  });
  return response.data;
}

// Get all available tools (entity_type='tool' only)
export async function getAllTools() {
  const response = await API.get(`/ai-models/models/`, {
    params: { entity_type: 'tool' }
  });
  return response.data;
}

// Get tools assigned to a study
export async function getToolsForStudy(studyId) {
  const response = await API.get(`/ai-models/models/`, {
    params: { study: studyId, entity_type: 'tool' }
  });
  return response.data;
}

// Get model details
export async function getModel(modelId) {
  const response = await API.get(`/ai-models/models/${modelId}/`);
  return response.data;
}

// Run a model on patient data
export async function runModel(modelId, params) {
  const response = await API.post(`/ai-models/models/${modelId}/run/`, params);
  return response.data;
}

// Get execution status
export async function getExecutionStatus(executionId) {
  const response = await API.get(`/ai-models/executions/${executionId}/`);
  return response.data;
}

// Get executions for a patient
export async function getExecutionsForPatient(patientId, studyId) {
  const params = { patient_id: patientId };
  if (studyId) params.study_id = studyId;

  const response = await API.get(`/ai-models/executions/`, { params });
  return response.data;
}

// Get active executions
export async function getActiveExecutions(patientId) {
  const params = { active: 'true' };
  if (patientId) params.patient_id = patientId;

  const response = await API.get(`/ai-models/executions/`, { params });
  return response.data;
}

// Get recent executions (including completed) for display in the tracker
export async function getRecentExecutions(patientId) {
  const params = {};
  if (patientId) params.patient_id = patientId;

  const response = await API.get(`/ai-models/executions/`, { params });
  return response.data;
}

// Cancel an execution
export async function cancelExecution(executionId) {
  const response = await API.post(`/ai-models/executions/${executionId}/cancel/`);
  return response.data;
}

// Get active execution count
export async function getActiveExecutionCount() {
  const response = await API.get(`/ai-models/executions/active-count/`);
  return response.data.active_count;
}

// Create a new AI model
export async function createModel(formData) {
  const response = await API.post(`/ai-models/models/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

// Update an AI model
export async function updateModel(modelId, data) {
  const response = await API.patch(`/ai-models/models/${modelId}/`, data);
  return response.data;
}

// Delete an AI model
export async function deleteModel(modelId) {
  const response = await API.delete(`/ai-models/models/${modelId}/`);
  return response.data;
}

// Build Docker image for a model
// sync=false (default) will use Celery async task if available
// sync=true will build synchronously (waits for build to complete) - NOT RECOMMENDED
export async function buildModelImage(modelId, sync = false) {
  const response = await API.post(`/ai-models/models/${modelId}/build/`, { sync });
  return response.data;
}

// Get build status for a model
export async function getBuildStatus(modelId) {
  const response = await API.get(`/ai-models/models/${modelId}/build-status/`);
  return response.data;
}

// Assign model to studies
export async function assignModelToStudies(modelId, studyIds) {
  const response = await API.post(`/ai-models/models/${modelId}/assign-studies/`, {
    study_ids: studyIds
  });
  return response.data;
}

// Get models for a project (all entity types)
export async function getModelsForProject(projectId) {
  const response = await API.get(`/ai-models/models/`, {
    params: { project: projectId }
  });
  return response.data;
}

// Get only AI models for a project (entity_type='model')
export async function getModelsOnlyForProject(projectId) {
  const response = await API.get(`/ai-models/models/`, {
    params: { project: projectId, entity_type: 'model' }
  });
  return response.data;
}

// Get only tools for a project (entity_type='tool')
export async function getToolsForProject(projectId) {
  const response = await API.get(`/ai-models/models/`, {
    params: { project: projectId, entity_type: 'tool' }
  });
  return response.data;
}

// Get only AI models for a study (entity_type='model')
export async function getModelsOnlyForStudy(studyId) {
  const response = await API.get(`/ai-models/models/`, {
    params: { study: studyId, entity_type: 'model' }
  });
  return response.data;
}

// Assign model to projects
export async function assignModelToProjects(modelId, projectIds) {
  const response = await API.post(`/ai-models/models/${modelId}/assign-projects/`, {
    project_ids: projectIds
  });
  return response.data;
}

// Add model to a project
export async function addModelToProject(modelId, projectId) {
  const response = await API.post(`/ai-models/models/${modelId}/add-project/`, {
    project_id: projectId
  });
  return response.data;
}

// Remove model from a project
export async function removeModelFromProject(modelId, projectId) {
  const response = await API.post(`/ai-models/models/${modelId}/remove-project/`, {
    project_id: projectId
  });
  return response.data;
}

// Add study to model
export async function addStudyToModel(modelId, studyId) {
  const response = await API.post(`/ai-models/models/${modelId}/add-study/`, {
    study_id: studyId
  });
  return response.data;
}

// Remove study from model
export async function removeStudyFromModel(modelId, studyId) {
  const response = await API.post(`/ai-models/models/${modelId}/remove-study/`, {
    study_id: studyId
  });
  return response.data;
}

// Get public models (for Open Medical Models page)
export async function getPublicModels() {
  const response = await API.get(`/ai-models/models/`, {
    params: { public: 'true' }
  });
  return response.data;
}

// Toggle model public visibility
export async function toggleModelPublic(modelId) {
  const response = await API.post(`/ai-models/models/${modelId}/toggle-public/`);
  return response.data;
}

// Add public model to user's models
export async function addModelToMyModels(modelId) {
  const response = await API.post(`/ai-models/models/${modelId}/add-to-my-models/`);
  return response.data;
}

// Remove model from user's models
export async function removeModelFromMyModels(modelId) {
  const response = await API.post(`/ai-models/models/${modelId}/remove-from-my-models/`);
  return response.data;
}

// Get versions for a model
export async function getModelVersions(modelId) {
  const response = await API.get(`/ai-models/models/${modelId}/versions/`);
  return response.data;
}

// Get version details
export async function getModelVersion(modelId, versionId) {
  const response = await API.get(`/ai-models/models/${modelId}/versions/${versionId}/`);
  return response.data;
}

// Set default version for a model
export async function setDefaultVersion(modelId, versionId) {
  const response = await API.post(`/ai-models/models/${modelId}/versions/${versionId}/set-default/`);
  return response.data;
}

// Build Docker image for a specific version
export async function buildVersionImage(modelId, versionId) {
  const response = await API.post(`/ai-models/models/${modelId}/versions/${versionId}/build/`);
  return response.data;
}

// Toggle version active state
export async function toggleVersionActive(modelId, versionId) {
  const response = await API.post(`/ai-models/models/${modelId}/versions/${versionId}/toggle-active/`);
  return response.data;
}

// Toggle keep_loaded (persistent server) for a model
export async function toggleKeepLoaded(modelId, enable, gpuDeviceIds = null) {
  const data = { enable };
  if (gpuDeviceIds !== null) data.gpu_device_ids = gpuDeviceIds;
  const response = await API.post(`/ai-models/models/${modelId}/toggle-keep-loaded/`, data);
  return response.data;
}

// Get persistent server status for a model
export async function getServerStatus(modelId) {
  const response = await API.get(`/ai-models/models/${modelId}/server-status/`);
  return response.data;
}

// Helper to check if execution is terminal
export function isTerminalStatus(status) {
  return ['completed', 'failed', 'cancelled'].includes(status);
}

// Helper to get status display info
export function getStatusDisplay(status) {
  const statusMap = {
    pending: { label: 'Pending', color: '#6c757d', icon: 'fa-clock' },
    preparing: { label: 'Preparing', color: '#17a2b8', icon: 'fa-cog fa-spin' },
    running: { label: 'Running', color: '#007bff', icon: 'fa-play' },
    converting: { label: 'Converting', color: '#fd7e14', icon: 'fa-sync fa-spin' },
    uploading: { label: 'Uploading', color: '#6f42c1', icon: 'fa-upload' },
    completed: { label: 'Completed', color: '#28a745', icon: 'fa-check' },
    failed: { label: 'Failed', color: '#dc3545', icon: 'fa-times' },
    cancelled: { label: 'Cancelled', color: '#6c757d', icon: 'fa-ban' },
  };
  return statusMap[status] || { label: status, color: '#6c757d', icon: 'fa-question' };
}

// Format duration in seconds to human readable
export function formatDuration(seconds) {
  if (seconds === null) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

export default {
  getModelsForStudy,
  getAllModels,
  getModel,
  runModel,
  getExecutionStatus,
  getExecutionsForPatient,
  getActiveExecutions,
  cancelExecution,
  getActiveExecutionCount,
  createModel,
  updateModel,
  deleteModel,
  buildModelImage,
  getBuildStatus,
  assignModelToStudies,
  getModelsForProject,
  getModelsOnlyForProject,
  getToolsForProject,
  getModelsOnlyForStudy,
  assignModelToProjects,
  addModelToProject,
  removeModelFromProject,
  addStudyToModel,
  removeStudyFromModel,
  getPublicModels,
  toggleModelPublic,
  addModelToMyModels,
  removeModelFromMyModels,
  getModelVersions,
  getModelVersion,
  setDefaultVersion,
  buildVersionImage,
  toggleVersionActive,
  toggleKeepLoaded,
  getServerStatus,
  isTerminalStatus,
  getStatusDisplay,
  formatDuration,
};
